import { useEffect, useState } from 'react';
import { apiFetch } from '../../../lib/api';
import { clientApprovalTypeLabel } from '../../../lib/clientApprovals';
import {
  type ClientApprovalHistoryItem, channelLabel, decisionLabel, formatDate, progressLabel,
} from '../../../lib/clientApprovalPortal';

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open', APPROVED: 'Approved', CHANGES_REQUESTED: 'Changes requested', SUPERSEDED: 'Superseded',
};

/**
 * #3187 APPROVE.4 — approval history (design v1.1 §5.4): past requests and
 * responses with their channel, from 3a's GET /client-approval-requests?projectId=
 * (its readers narrow in 3b, ruling 6550647304 item 3; a refusal shows the
 * failure line, never someone else's data).
 */
export default function ClientApprovalHistory({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<ClientApprovalHistoryItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<ClientApprovalHistoryItem[]>(`/client-approval-requests?projectId=${encodeURIComponent(projectId)}`)
      .then((r) => { if (!cancelled) setItems(Array.isArray(r) ? r : []); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [projectId]);

  return (
    <section data-testid="client-approval-history" style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Approval History</h2>
      {failed ? (
        <p data-testid="client-approval-history-failed" style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>Approval history couldn&apos;t be loaded.</p>
      ) : items === null ? (
        <p style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>Loading...</p>
      ) : items.length === 0 ? (
        <p data-testid="client-approval-history-empty" style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>No approval requests yet.</p>
      ) : (
        items.map((r) => (
          <div key={r.requestId} data-testid="client-approval-history-item" style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {r.title} · version {r.version} — {STATUS_LABEL[r.status] ?? r.status}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>
              {clientApprovalTypeLabel(r.artefactType)} · requested {formatDate(r.openedAt)}
              {r.closedAt ? ` · closed ${formatDate(r.closedAt)}` : ''} · {progressLabel(r.approvedCount, r.approverCount)}
              {r.atRisk ? ' · proceeding at risk' : ''}
            </div>
            {r.responses.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                {r.responses.map((x) => (
                  <li key={`${r.requestId}-${x.stakeholderId}`} data-testid="client-approval-history-response">
                    {x.name ?? 'An approver'}: {decisionLabel(x.decision)} via{' '}
                    <span data-testid="client-approval-history-channel">{channelLabel(x.channel)}</span> on {formatDate(x.respondedAt)}
                    {x.comment ? ` — “${x.comment}”` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </section>
  );
}
